import api from './api.ts';

export interface McpOAuthClientSummary {
  client_id: string;
  client_name: string;
  client_uri?: string | null;
  redirect_host: string;
}

export interface McpAllowedClinic {
  id: number;
  name: string;
  logo_url?: string | null;
}

export interface McpAuthorizationRequest {
  request_id: string;
  client: McpOAuthClientSummary;
  requested_scopes: string[];
  allowed_clinics: McpAllowedClinic[];
  expires_at: string;
}

export interface McpAuthorizationDecision {
  redirect_url: string;
}

export type McpConnectionStatus = 'active' | 'revoked' | 'expired' | string;

export interface McpConnection {
  id: string | number;
  client_name: string;
  clinic_id: number;
  clinic_name: string;
  clinics?: McpAllowedClinic[];
  scopes: string[];
  status: McpConnectionStatus;
  created_at: string;
  last_used_at?: string | null;
  revoked_at?: string | null;
}

interface McpConnectionsResponse {
  connections: McpConnection[];
}

const getErrorDetail = (error: unknown): unknown => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }

  return (
    error as {
      response?: {
        data?: {
          detail?: unknown;
        };
      };
    }
  ).response?.data?.detail;
};

export const getMcpErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const detail = getErrorDetail(error);

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (
    detail
    && typeof detail === 'object'
    && 'message' in detail
    && typeof (detail as { message?: unknown }).message === 'string'
  ) {
    return (detail as { message: string }).message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

export const getMcpAuthorizationRequest = async (
  requestId: string,
): Promise<McpAuthorizationRequest> => {
  const response = await api.get<McpAuthorizationRequest>(
    `/api/mcp/oauth/authorize/requests/${encodeURIComponent(requestId)}`,
  );
  return response.data;
};

export const approveMcpAuthorizationRequest = async (
  requestId: string,
  clinicIds: number[],
): Promise<McpAuthorizationDecision> => {
  const response = await api.post<McpAuthorizationDecision>(
    `/api/mcp/oauth/authorize/requests/${encodeURIComponent(requestId)}/approve`,
    { clinic_ids: clinicIds },
  );
  return response.data;
};

export const denyMcpAuthorizationRequest = async (
  requestId: string,
): Promise<McpAuthorizationDecision> => {
  const response = await api.post<McpAuthorizationDecision>(
    `/api/mcp/oauth/authorize/requests/${encodeURIComponent(requestId)}/deny`,
  );
  return response.data;
};

export const listMcpConnections = async (): Promise<McpConnection[]> => {
  const response = await api.get<McpConnectionsResponse>('/api/mcp/connections');
  return response.data.connections;
};

export const revokeMcpConnection = async (grantId: string | number): Promise<void> => {
  await api.delete(`/api/mcp/connections/${encodeURIComponent(grantId)}`);
};
